<style>
@import "@/assets/css/custom.css";
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="Verify Courier Payment" />

		<div class="container-fluid">
			<div v-if="loading" class="card">
				<div class="card-body text-center py-5">
					<span class="spinner-border spinner-border-sm me-2"></span>
					Loading courier details…
				</div>
			</div>

			<div v-else-if="loadError" class="card">
				<div class="card-body text-center py-5">
					<p class="text-danger mb-3">{{ loadError }}</p>
					<button type="button" class="btn btn-light" @click="$router.push({ name: 'courier_account' })">Back to courier list</button>
				</div>
			</div>

			<div v-else-if="courier" class="card">
				<div class="card-body">
					<div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
						<div>
							<h5 class="mb-1">Courier #{{ courier.id }}</h5>
							<div class="small text-muted">
								<div><strong>Party:</strong> {{ userTypeLabel(form.user_type) }} — {{ courier.request_user_name || "—" }}</div>
								<div><strong>Contact:</strong> {{ courier.request_user_contact || "—" }}</div>
								<div><strong>Total devices:</strong> {{ courier.total_devices ?? "—" }}</div>
								<div><strong>Recorded:</strong> {{ courier.created_at ? $filters.formatDate(courier.created_at) : "—" }}</div>
							</div>
						</div>
						<button type="button" class="btn btn-light btn-sm" @click="$router.push({ name: 'courier_account' })">Back</button>
					</div>

					<div class="card border-light mb-4">
						<div class="card-body py-3">
							<div class="row text-center small">
								<div class="col-md-2">
									<span class="text-muted d-block">Grand total</span>
									<span class="fw-bold">₹{{ formatMoney(summary?.grand_total ?? grandTotals.total_amount) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Paid</span>
									<span class="fw-bold text-success">₹{{ formatMoney(summary?.paid_amount) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Pending</span>
									<span class="fw-bold text-warning">₹{{ formatMoney(pendingAmount) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Online paid</span>
									<span class="fw-bold">₹{{ formatMoney(summary?.online_amount) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Cash paid</span>
									<span class="fw-bold">₹{{ formatMoney(summary?.cash_in_hand) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Payment status</span>
									<span class="badge rounded-pill" :class="paymentStatusClass(summary?.payment_status)">
										{{ summary?.payment_status_label || paymentStatusLabel(summary?.payment_status) }}
									</span>
								</div>
							</div>
						</div>
					</div>

					<form @submit.prevent="submit">
						<div class="row">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">User type</label>
								<input class="form-control" type="text" :value="userTypeLabel(form.user_type)" readonly />
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">User</label>
								<input class="form-control" type="text" :value="courier.request_user_name || '—'" readonly />
							</div>
							<div class="col-md-4 form-group mt-2" v-if="form.user_type === '6'">
								<label class="control-label">Technician<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.user_id">
									<option value="">— Select technician —</option>
									<option v-for="t in technicians" :key="'tech-' + t.id" :value="String(t.id)">{{ t.name }}</option>
								</select>
								<div v-if="errors.user_id" class="text-danger small mt-1">{{ errors.user_id }}</div>
							</div>
						</div>

						<div class="mt-4">
							<h6 class="border-bottom pb-2">Products</h6>
							<div class="table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th>Product type</th>
											<th>Quantity</th>
											<th>Amount (₹)</th>
											<th>GST (₹)</th>
											<th>Total (₹)</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(row, idx) in productRows" :key="'pr-' + row.product_type + '-' + idx" align="center">
											<td class="text-start">{{ row.label }}</td>
											<td>{{ row.quantity }}</td>
											<td>
												<input
													class="form-control form-control-sm"
													type="number"
													min="0"
													step="0.01"
													v-model="row.amount"
													@input="syncProductRow(idx)"
												/>
											</td>
											<td>
												<input
													class="form-control form-control-sm"
													type="number"
													min="0"
													step="0.01"
													v-model="row.gst_amount"
													@input="syncProductRow(idx)"
												/>
											</td>
											<td>
												<input class="form-control form-control-sm" type="number" min="0" step="0.01" v-model="row.total_amount" readonly />
											</td>
										</tr>
									</tbody>
									<tfoot class="table-light">
										<tr align="center">
											<th colspan="2" class="text-end">Grand total</th>
											<th>₹{{ formatMoney(grandTotals.amount) }}</th>
											<th>₹{{ formatMoney(grandTotals.gst_amount) }}</th>
											<th>₹{{ formatMoney(grandTotals.total_amount) }}</th>
										</tr>
									</tfoot>
								</table>
							</div>
							<div v-if="errors.products" class="text-danger small">{{ errors.products }}</div>
						</div>

						<div class="row mt-2">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Invoice number</label>
								<input class="form-control" type="text" v-model="form.invoice_number" placeholder="Optional" />
							</div>
						</div>

						<div class="mt-4 mb-4">
							<h6 class="border-bottom pb-2">Payment logs</h6>
							<p v-if="!paymentLogs.length" class="text-muted mb-0">No payment logs for this courier.</p>
							<div v-else class="table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th>#</th>
											<th>Transaction amount (₹)</th>
											<th>Status</th>
											<th>Pending (₹)</th>
											<th>Credit (₹)</th>
											<th>Total (₹)</th>
											<th>Bank</th>
											<th>Payment ID</th>
											<th>Payment date</th>
											<th>Recorded on</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(log, idx) in paymentLogs" :key="'vpl-' + log.id" align="center">
											<td>{{ idx + 1 }}</td>
											<td>₹{{ formatMoney(log.transaction_amount) }}</td>
											<td>
												<span class="badge rounded-pill" :class="paymentStatusClass(log.status)">
													{{ log.status_label || paymentStatusLabel(log.status) }}
												</span>
											</td>
											<td>₹{{ formatMoney(log.pending_amount) }}</td>
											<td>₹{{ formatMoney(log.credit_amount) }}</td>
											<td>₹{{ formatMoney(log.total_amount) }}</td>
											<td class="text-start small">{{ log.bank_name || "—" }}</td>
											<td class="text-start small">{{ log.payment_id || "—" }}</td>
											<td>{{ log.payment_date ? $filters.formatDate(log.payment_date) : "—" }}</td>
											<td class="text-start small">{{ log.created_at ? $filters.formatDate(log.created_at) : "—" }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<div v-if="isDealerOrCustomer" class="mt-4">
							<h6 class="border-bottom pb-2">Record payment</h6>
							<div class="row">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Payment status<span class="text-danger">*</span></label>
									<select class="form-select" v-model="form.payment_status" @change="onPaymentStatusChange">
										<option value="">— Select —</option>
										<option value="1">Received</option>
										<option value="2">Not received</option>
										<option value="4">Advance</option>
									</select>
									<div v-if="errors.payment_status" class="text-danger small mt-1">{{ errors.payment_status }}</div>
								</div>

								<div class="col-md-4 form-group mt-2" v-if="form.payment_status === '4'">
									<label class="control-label">Amount from advance<span class="text-danger">*</span></label>
									<input class="form-control" type="number" min="0" step="0.01" v-model="form.amount_from_credit" placeholder="0.00" />
									<div v-if="userCreditLoading" class="mt-1"><small class="text-muted">Loading advance balance…</small></div>
									<div v-else-if="userCredit && userCredit.credit_available_amount != null" class="mt-1">
										<small class="text-success">Advance available: ₹{{ userCredit.credit_available_amount }}</small>
									</div>
									<div v-if="userCreditError" class="text-danger small mt-1">{{ userCreditError }}</div>
									<div v-if="errors.amount_from_credit" class="text-danger small mt-1">{{ errors.amount_from_credit }}</div>
								</div>
							</div>

							<div class="row" v-if="form.payment_status === '1'">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Payment type<span class="text-danger">*</span></label>
									<select class="form-select" v-model="form.payment_type" @change="onPaymentTypeChange">
										<option value="">— Select —</option>
										<option value="1">Cash</option>
										<option value="2">Online</option>
										<option value="3">Both</option>
									</select>
									<div v-if="errors.payment_type" class="text-danger small mt-1">{{ errors.payment_type }}</div>
								</div>
							</div>

							<div class="row" v-if="form.payment_status === '1' && (form.payment_type === '2' || form.payment_type === '3')">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Bank account<span class="text-danger">*</span></label>
									<select class="form-select" v-model="form.bank_account_id">
										<option value="">— Select bank —</option>
										<option v-for="b in bankAccounts" :key="b.id" :value="String(b.id)">
											{{ b.bank_name }} — {{ b.account_number }}
										</option>
									</select>
									<small class="text-muted">Required for online and both payments.</small>
									<div v-if="errors.bank_account_id" class="text-danger small mt-1">{{ errors.bank_account_id }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Online amount (₹)<span class="text-danger">*</span></label>
									<input class="form-control" type="number" min="0" step="0.01" v-model="form.cash_online" placeholder="0.00" />
									<div v-if="errors.cash_online" class="text-danger small mt-1">{{ errors.cash_online }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Payment / transaction ID</label>
									<input class="form-control" type="text" v-model="form.payment_id" placeholder="UTR / reference" />
									<div v-if="errors.payment_id" class="text-danger small mt-1">{{ errors.payment_id }}</div>
								</div>
							</div>

							<div class="row" v-if="form.payment_status === '1' && (form.payment_type === '1' || form.payment_type === '3')">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Cash Amount (₹)<span class="text-danger">*</span></label>
									<input class="form-control" type="number" min="0" step="0.01" v-model="form.cash_in_hand" placeholder="0.00" />
									<div v-if="errors.cash_in_hand" class="text-danger small mt-1">{{ errors.cash_in_hand }}</div>
								</div>
							</div>

							<div class="row" v-if="recordPaymentFooter">
								<div class="col-md-12">
									<div class="card border-light mt-2">
										<div class="card-body py-2">
											<div class="row text-center small g-2">
												<div class="col-6 col-md">
													<span class="text-muted d-block">Sale total</span>
													<span class="fw-bold">₹{{ recordPaymentFooter.total }}</span>
												</div>
												<div class="col-6 col-md">
													<span class="text-muted d-block">Paid</span>
													<span class="fw-bold text-success">₹{{ recordPaymentFooter.paid }}</span>
												</div>
												<div class="col-6 col-md">
													<span class="text-muted d-block">Pending</span>
													<span class="fw-bold text-warning">₹{{ recordPaymentFooter.pending }}</span>
												</div>
												<div class="col-6 col-md" v-if="recordPaymentFooter.online != null">
													<span class="text-muted d-block">This payment (online)</span>
													<span class="fw-bold">₹{{ recordPaymentFooter.online }}</span>
												</div>
												<div class="col-6 col-md" v-if="recordPaymentFooter.cash != null">
													<span class="text-muted d-block">This payment (cash)</span>
													<span class="fw-bold">₹{{ recordPaymentFooter.cash }}</span>
												</div>
												<div class="col-6 col-md" v-if="recordPaymentFooter.statusLabel">
													<span class="text-muted d-block">Match (vs pending)</span>
													<span class="fw-bold" :class="recordPaymentFooter.statusClass">{{ recordPaymentFooter.statusLabel }}</span>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div class="row">
							<div v-if="showPaymentDate" class="col-md-4 form-group mt-2">
								<label class="control-label">Payment date<span v-if="isPaymentDateRequired" class="text-danger">*</span></label>
								<input class="form-control" type="date" v-model="form.payment_date" />
								<div v-if="errors.payment_date" class="text-danger small mt-1">{{ errors.payment_date }}</div>
							</div>
						</div>

						<div class="row">
							<div class="col-md-12 form-group mt-3">
								<label class="control-label">Remarks</label>
								<textarea class="form-control" rows="2" v-model="form.remarks" placeholder="Optional remarks"></textarea>
							</div>
						</div>

						<div class="row mt-4">
							<div class="col-12">
								<button type="submit" class="btn btn-primary" :disabled="submitting">
									<span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									{{ submitting ? "Saving..." : "Verify & complete payment" }}
								</button>
								<button type="button" class="btn btn-light ms-2" :disabled="submitting" @click="$router.push({ name: 'courier_account' })">
									Cancel
								</button>
							</div>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "VerifyCourier",
	data() {
		return {
			token: "",
			courierId: null,
			loading: true,
			loadError: "",
			submitting: false,
			errors: {},

			courier: null,
			summary: null,
			paymentLogs: [],
			productRows: [],

			bankAccounts: [],
			technicians: [],

			userCredit: null,
			userCreditLoading: false,
			userCreditError: "",

			form: {
				user_type: "",
				user_id: "",
				invoice_number: "",
				payment_status: "",
				payment_type: "",
				cash_online: "",
				cash_in_hand: "",
				amount_from_credit: "",
				bank_account_id: "",
				payment_id: "",
				payment_date: "",
				remarks: "",
			},
		};
	},
	computed: {
		pendingAmount() {
			if (this.summary?.pending_amount != null && this.summary.pending_amount !== "") {
				return parseFloat(this.summary.pending_amount) || 0;
			}
			const grand = parseFloat(this.summary?.grand_total) || this.grandTotals.total_amount || 0;
			const paid = parseFloat(this.summary?.paid_amount) || 0;
			return Math.max(0, grand - paid);
		},
		isDealerOrCustomer() {
			return this.form.user_type === "1" || this.form.user_type === "2";
		},
		showPaymentDate() {
			if (!this.isDealerOrCustomer) return false;
			return this.form.payment_status === "1" || this.form.payment_status === "3" || this.form.payment_status === "4";
		},
		isPaymentDateRequired() {
			return this.showPaymentDate;
		},
		grandTotals() {
			let amount = 0;
			let gst_amount = 0;
			let total_amount = 0;
			for (const row of this.productRows) {
				amount += parseFloat(row.amount) || 0;
				gst_amount += parseFloat(row.gst_amount) || 0;
				total_amount += parseFloat(row.total_amount) || 0;
			}
			return { amount, gst_amount, total_amount };
		},
		recordPaymentFooter() {
			if (!this.isDealerOrCustomer) return null;
			const total =
				parseFloat(this.summary?.grand_total) ||
				this.grandTotals.total_amount ||
				0;
			const paidSoFar = parseFloat(this.summary?.paid_amount) || 0;
			const pending =
				this.summary?.pending_amount != null && this.summary.pending_amount !== ""
					? parseFloat(this.summary.pending_amount) || 0
					: Math.max(0, total - paidSoFar);

			const footer = {
				total: this.formatMoney(total),
				paid: this.formatMoney(paidSoFar),
				pending: this.formatMoney(pending),
				online: null,
				cash: null,
				statusLabel: null,
				statusClass: null,
			};

			if (this.form.payment_status !== "1" || !this.form.payment_type) {
				return footer;
			}

			const online = parseFloat(this.form.cash_online) || 0;
			const cash = parseFloat(this.form.cash_in_hand) || 0;
			let thisPayment = 0;
			if (this.form.payment_type === "2") thisPayment = online;
			else if (this.form.payment_type === "1") thisPayment = cash;
			else if (this.form.payment_type === "3") thisPayment = online + cash;

			const eps = 0.01;
			const compareTo = pending > 0 ? pending : total;
			const diff = thisPayment - compareTo;
			let statusLabel = "Partial";
			let statusClass = "text-warning";
			if (Math.abs(diff) < eps) {
				statusLabel = "Full";
				statusClass = "text-success";
			} else if (diff > eps) {
				statusLabel = "Over";
				statusClass = "text-info";
			}

			if (this.form.payment_type === "2" || this.form.payment_type === "3") {
				footer.online = online.toFixed(2);
			}
			if (this.form.payment_type === "1" || this.form.payment_type === "3") {
				footer.cash = cash.toFixed(2);
			}
			footer.statusLabel = statusLabel;
			footer.statusClass = statusClass;
			return footer;
		},
	},
	watch: {
		"form.payment_status"() {
			this.maybeFetchUserCredit();
		},
	},
	created() {
		this.courierId = this.$route.params.courier_id;
		if (!this.courierId) {
			this.loading = false;
			this.loadError = "Courier ID is missing";
			return;
		}
		this.init();
	},
	methods: {
		getToken() {
			if (localStorage.getItem("airotrack_staff")) {
				const staff = JSON.parse(localStorage.getItem("airotrack_staff"));
				if (staff.token) {
					this.token = staff.token;
					return;
				}
			}
			this.$swal("", "Session expired, please login again", "error");
			this.$router.push({ name: "login" });
		},

		async init() {
			this.getToken();
			try {
				await Promise.all([this.fetchBankAccounts(), this.fetchTechnicians(), this.fetchCourierDetails()]);
			} catch (e) {
				this.loadError = e?.message || "Failed to load courier details";
			} finally {
				this.loading = false;
			}
		},

		fetchCourierDetails() {
			return this.$axios
				.get(`/staffs/courierDetails?courier_id=${encodeURIComponent(this.courierId)}`, {
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((res) => {
					const data = res.data?.data || {};
					this.courier = data.courier || null;
					this.summary = data.summary || null;
					this.paymentLogs = Array.isArray(data.payment_logs) ? data.payment_logs : [];
					if (!this.courier) {
						throw new Error("Courier not found");
					}
					this.productRows = this.buildProductRows(data.products || []);
					this.applyCourierToForm(this.courier, data.products || []);
				})
				.catch((err) => {
					throw new Error(err.response?.data?.message || "Failed to load courier details");
				});
		},

		fetchBankAccounts() {
			return this.$axios
				.get("/staffs/bankAccounts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.bankAccounts = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.bankAccounts = [];
				});
		},

		fetchTechnicians() {
			return this.$axios
				.get("/staffs/technicians", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data?.data || res.data || {};
					const list = Array.isArray(data?.technicians) ? data.technicians : Array.isArray(data) ? data : [];
					this.technicians = list;
				})
				.catch(() => {
					this.technicians = [];
				});
		},

		buildProductRows(products) {
			const map = {};
			for (const p of products) {
				const pt = String(p.product_type);
				if (!map[pt]) {
					map[pt] = {
						product_type: pt,
						label: p.product_type_label || this.productTypeLabel(pt),
						quantity: 0,
						amount: "0",
						gst_amount: "0",
						total_amount: "0",
					};
				}
				map[pt].quantity += parseInt(p.quantity, 10) || 0;
				const amt = parseFloat(p.amount) || 0;
				const gst = parseFloat(p.gst) || 0;
				const tot = parseFloat(p.total) || 0;
				map[pt].amount = String((parseFloat(map[pt].amount) || 0) + amt);
				map[pt].gst_amount = String((parseFloat(map[pt].gst_amount) || 0) + gst);
				map[pt].total_amount = String((parseFloat(map[pt].total_amount) || 0) + tot);
			}
			return Object.values(map).map((row) => ({
				...row,
				amount: this.formatMoney(row.amount),
				gst_amount: this.formatMoney(row.gst_amount),
				total_amount: this.formatMoney(row.total_amount),
			}));
		},

		resolveUserType(courier) {
			if (courier.request_user_type != null && courier.request_user_type !== "") {
				return String(courier.request_user_type);
			}
			if (courier.dealer_id != null && courier.dealer_id !== "") return "1";
			if (courier.customer_id != null && courier.customer_id !== "") return "2";
			if (courier.technician_id != null && courier.technician_id !== "") return "6";
			return "";
		},

		applyCourierToForm(courier, products) {
			this.form.user_type = this.resolveUserType(courier);
			this.form.user_id = courier.request_user_id != null ? String(courier.request_user_id) : "";
			this.form.invoice_number = courier.invoice_number || "";

			const paymentProduct = products.find((p) => p.has_payment && p.payment);
			const payment = paymentProduct?.payment;
			const txn = paymentProduct?.payment_transaction;

			if (payment) {
				if (payment.payment_date) this.form.payment_date = String(payment.payment_date).slice(0, 10);
				if (payment.remarks) this.form.remarks = payment.remarks;
			}

			if (txn?.invoice_date && !this.form.payment_date) {
				this.form.payment_date = String(txn.invoice_date).slice(0, 10);
			}
			if (txn?.invoice_number && !this.form.invoice_number) {
				this.form.invoice_number = txn.invoice_number;
			}

			if (this.isDealerOrCustomer) {
				this.maybeFetchUserCredit();
			}
		},

		async maybeFetchUserCredit() {
			if (this.form.payment_status !== "4" || !this.form.user_id) {
				this.userCredit = null;
				this.userCreditError = "";
				this.userCreditLoading = false;
				return;
			}
			try {
				this.userCreditLoading = true;
				this.userCreditError = "";
				const res = await this.$axios.get("/staffs/getUserCredit", {
					params: { user_id: this.form.user_id },
					headers: { Authorization: "Bearer " + this.token },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (!ok) {
					this.userCredit = null;
					this.userCreditError = res.data?.message || "Failed to fetch advance balance";
					return;
				}
				this.userCredit = res.data?.data?.user_credit || null;
			} catch (e) {
				this.userCredit = null;
				this.userCreditError = e?.response?.data?.message || "Failed to fetch advance balance";
			} finally {
				this.userCreditLoading = false;
			}
		},

		onPaymentStatusChange() {
			if (this.form.payment_status !== "1") {
				this.form.payment_type = "";
				this.form.cash_online = "";
				this.form.cash_in_hand = "";
				this.form.payment_id = "";
				this.form.bank_account_id = "";
			}
			if (this.form.payment_status === "2") {
				this.form.payment_date = "";
				delete this.errors.payment_date;
			}
			if (this.form.payment_status !== "4") {
				this.form.amount_from_credit = "";
			} else if (!String(this.form.amount_from_credit || "").trim()) {
				this.form.amount_from_credit = this.formatMoney(this.grandTotals.total_amount);
			}
			this.maybeFetchUserCredit();
		},

		onPaymentTypeChange() {
			if (this.form.payment_type === "2") {
				this.form.cash_in_hand = "";
			} else if (this.form.payment_type === "1") {
				this.form.cash_online = "";
				this.form.payment_id = "";
				this.form.bank_account_id = "";
			}
		},

		syncProductRow(idx) {
			const row = this.productRows[idx];
			if (!row) return;
			const a = parseFloat(row.amount);
			const g = parseFloat(row.gst_amount);
			const hasA = !isNaN(a);
			const hasG = !isNaN(g);
			row.total_amount = ((hasA ? a : 0) + (hasG ? g : 0)).toFixed(2);
		},

		productTypeLabel(t) {
			const map = { 2: "GPS", 4: "Camera", 5: "Speed Governor" };
			return map[String(t)] || "Product";
		},

		userTypeLabel(t) {
			const map = { 1: "Dealer", 2: "Customer", 6: "Technician" };
			return map[String(t)] || "—";
		},

		paymentStatusLabel(v) {
			const map = { 1: "Received", 2: "Not received", 3: "Partially received", 4: "Advance" };
			return map[Number(v)] || "—";
		},

		paymentStatusClass(v) {
			const n = Number(v);
			if (n === 1) return "bg-success";
			if (n === 2) return "bg-danger";
			if (n === 3) return "bg-warning";
			if (n === 4) return "bg-info text-dark";
			return "bg-secondary";
		},

		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},

		validate() {
			this.errors = {};

			if (!this.productRows.length) {
				this.errors.products = "No products on this courier";
				return false;
			}

			for (let i = 0; i < this.productRows.length; i++) {
				this.syncProductRow(i);
				const row = this.productRows[i];
				const amount = parseFloat(row.amount);
				const gst = parseFloat(row.gst_amount);
				const total = parseFloat(row.total_amount);
				if (isNaN(amount) || amount < 0) {
					this.errors.products = `Enter valid amount for ${row.label}`;
					return false;
				}
				if (isNaN(gst) || gst < 0) {
					this.errors.products = `Enter valid GST for ${row.label}`;
					return false;
				}
				if (isNaN(total) || total < 0) {
					this.errors.products = `Enter valid total for ${row.label}`;
					return false;
				}
			}

			if (!this.form.user_type) {
				this.errors.user_type = "User type is missing on courier";
				return false;
			}
			if (!this.form.user_id) {
				this.errors.user_id = "User is missing on courier";
				return false;
			}

			if (this.isDealerOrCustomer) {
				if (!this.form.payment_status) {
					this.errors.payment_status = "Select payment status";
					return false;
				}
				if (this.form.payment_status === "1") {
					if (!this.form.payment_type) {
						this.errors.payment_type = "Select payment type when payment is received";
						return false;
					}
					const pt = this.form.payment_type;
					if (pt === "2" || pt === "3") {
						if (!this.form.bank_account_id) {
							this.errors.bank_account_id = "Select bank account";
							return false;
						}
						const co = parseFloat(this.form.cash_online);
						if (isNaN(co) || co <= 0) {
							this.errors.cash_online = "Enter a valid online amount";
							return false;
						}
						// if (!String(this.form.payment_id || "").trim()) {
						// 	this.errors.payment_id = "Enter payment / transaction ID";
						// 	return false;
						// }
					}
					if (pt === "1" || pt === "3") {
						const ch = parseFloat(this.form.cash_in_hand);
						if (isNaN(ch) || ch <= 0) {
							this.errors.cash_in_hand = "Enter a valid cash in hand amount";
							return false;
						}
					}
				}
				if (this.form.payment_status === "4") {
					const ac = parseFloat(this.form.amount_from_credit);
					const grandTotal = this.grandTotals.total_amount;
					if (isNaN(ac) || ac <= 0) {
						this.errors.amount_from_credit = "Enter amount from advance";
						return false;
					}
					if (this.userCredit && this.userCredit.credit_available_amount != null) {
						const avail = parseFloat(this.userCredit.credit_available_amount);
						if (!isNaN(avail) && ac > avail) {
							this.errors.amount_from_credit = "Amount cannot exceed available advance";
							return false;
						}
					}
					if (!isNaN(grandTotal) && !isNaN(ac) && ac > grandTotal) {
						this.errors.amount_from_credit = "Amount cannot exceed sale total";
						return false;
					}
				}
				if (this.isPaymentDateRequired && !this.form.payment_date) {
					this.errors.payment_date = "Select payment date";
					return false;
				}
			}

			return true;
		},

		buildPayload() {
			const gt = this.grandTotals;
			const payload = {
				courier_id: parseInt(this.courierId, 10),
				user_type: parseInt(this.form.user_type, 10),
				user_id: parseInt(this.form.user_id, 10),
				products: this.productRows.map((row) => ({
					product_type: parseInt(row.product_type, 10),
					amount: parseFloat(row.amount),
					gst_amount: parseFloat(row.gst_amount),
					total_amount: parseFloat(row.total_amount),
				})),
				amount: gt.amount,
				gst_amount: gt.gst_amount,
				total_amount: gt.total_amount,
				bank_account_id: null,
				payment_id: null,
				payment_date: String(this.form.payment_date || "").trim() || null,
				remarks: String(this.form.remarks || "").trim() || null,
				invoice_number: String(this.form.invoice_number || "").trim() || null,
				payment_status: null,
				payment_type: null,
				cash_online: null,
				cash_in_hand: null,
				amount_from_credit: null,
			};

			if (this.isDealerOrCustomer) {
				payload.payment_status = parseInt(this.form.payment_status, 10);
				const ps = this.form.payment_status;
				if (ps === "1") {
					payload.payment_type = parseInt(this.form.payment_type, 10);
					const pt = this.form.payment_type;
					if (pt === "2" || pt === "3") {
						payload.bank_account_id = parseInt(this.form.bank_account_id, 10);
						payload.cash_online = parseFloat(this.form.cash_online);
						payload.payment_id = String(this.form.payment_id || "").trim() || null;
					}
					if (pt === "1" || pt === "3") {
						payload.cash_in_hand = parseFloat(this.form.cash_in_hand);
					}
				} else if (ps === "4") {
					payload.amount_from_credit = parseFloat(this.form.amount_from_credit);
				}
			}

			return payload;
		},

		async submit() {
			if (this.submitting) return;
			if (this.isDealerOrCustomer && this.form.payment_status === "4") {
				await this.maybeFetchUserCredit();
			}
			if (!this.validate()) return;

			this.submitting = true;
			this.getToken();
			try {
				const payload = this.buildPayload();
				const res = await this.$axios.post("/staffs/verifyCourier", payload, {
					headers: { Authorization: "Bearer " + this.token, "Content-Type": "application/json" },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (ok) {
					this.$swal("Success", this.$extractApiMessage(res.data?.message) || "Courier payment verified", "success");
					this.$router.push({ name: "courier_account" });
				} else {
					this.$applyApiFieldErrors(this.errors, res.data);
					this.$swal("Error", this.$getApiErrorMessage(res.data, "Failed to verify courier payment"), "error");
				}
			} catch (err) {
				this.$applyApiFieldErrors(this.errors, err);
				this.$swal("Error", this.$getApiErrorMessage(err, "Something went wrong"), "error");
			} finally {
				this.submitting = false;
			}
		},
	},
};
</script>
